import { toBase64Url } from './bytes.js';
import { decodeCbor } from './cbor.js';
import { COSE_ALG, jwkToSpki } from './keys.js';
// COSE_Key labels (RFC 9052/9053).
const KTY = 1;
const ALG = 3;
const CRV = -1; // EC2/OKP curve, RSA: n
const X = -2; // EC2/OKP x, RSA: e
const Y = -3;
const KTY_OKP = 1;
const KTY_EC2 = 2;
const KTY_RSA = 3;
const CRV_P256 = 1;
const CRV_ED25519 = 6;
function bytesAt(map, label) {
    const v = map.get(label);
    if (!(v instanceof Uint8Array))
        throw new Error(`COSE: label ${label} must be a byte string`);
    return v;
}
/** Parse a COSE_Key (as found in WebAuthn attestedCredentialData) into a JWK + algorithm. */
export function coseToJwk(cose) {
    const map = cose instanceof Map ? cose : decodeCbor(cose);
    if (!(map instanceof Map))
        throw new Error('COSE: key must be a map');
    const kty = map.get(KTY);
    const alg = map.get(ALG);
    switch (kty) {
        case KTY_EC2: {
            if (alg !== COSE_ALG.ES256)
                throw new Error(`COSE: unsupported EC2 alg ${String(alg)}`);
            if (map.get(CRV) !== CRV_P256)
                throw new Error('COSE: only P-256 is supported');
            const x = bytesAt(map, X);
            const y = bytesAt(map, Y);
            if (x.length !== 32 || y.length !== 32)
                throw new Error('COSE: bad P-256 coordinate length');
            return {
                alg: COSE_ALG.ES256,
                jwk: { kty: 'EC', crv: 'P-256', x: toBase64Url(x), y: toBase64Url(y) },
            };
        }
        case KTY_OKP: {
            if (alg !== COSE_ALG.EdDSA && alg !== COSE_ALG.Ed25519) {
                throw new Error(`COSE: unsupported OKP alg ${String(alg)}`);
            }
            if (map.get(CRV) !== CRV_ED25519)
                throw new Error('COSE: only Ed25519 is supported');
            const x = bytesAt(map, X);
            if (x.length !== 32)
                throw new Error('COSE: bad Ed25519 key length');
            return { alg, jwk: { kty: 'OKP', crv: 'Ed25519', x: toBase64Url(x) } };
        }
        case KTY_RSA: {
            if (alg !== COSE_ALG.RS256)
                throw new Error(`COSE: unsupported RSA alg ${String(alg)}`);
            const n = bytesAt(map, CRV);
            const e = bytesAt(map, X);
            return { alg: COSE_ALG.RS256, jwk: { kty: 'RSA', n: toBase64Url(n), e: toBase64Url(e) } };
        }
        default:
            throw new Error(`COSE: unsupported kty ${String(kty)}`);
    }
}
/** COSE_Key → DER SubjectPublicKeyInfo (validates the key via WebCrypto import). */
export async function coseToSpki(cose) {
    const { jwk, alg } = coseToJwk(cose);
    return jwkToSpki(jwk, alg);
}
//# sourceMappingURL=cose.js.map